import type {
  IdentityMethod,
  PrivacyRight,
  RequestChannel,
  RequestOutcome,
  RequestSearchCriterion,
  RequestStatus,
} from '@erp/shared';
import { keepPreviousData, useQuery, type QueryClient } from '@tanstack/react-query';

import { api } from './api.ts';

// Mirrors apps/api/src/privacy/requests.service.ts.

/** What whoever registered a request (or sees the contact) may know. */
export interface RequestBrief {
  id: string;
  folio: number;
  right: PrivacyRight;
  status: RequestStatus;
  receivedAt: string;
  dueOn: string;
  outcome: RequestOutcome | null;
  closedAt: string | null;
}

export interface RequestSummary extends RequestBrief {
  channel: RequestChannel;
  requesterName: string;
  contact: { id: string; name: string } | null;
  /** Business days left (negative: overdue); null when closed or stopped. */
  daysLeft: number | null;
  extended: boolean;
}

export interface RequestDetail extends RequestSummary {
  replyTo: string | null;
  description: string;
  prevention: {
    note: string;
    startedAt: string;
    dueOn: string;
    answeredAt: string | null;
    paused: boolean;
  } | null;
  extensionReason: string | null;
  identity: { method: IdentityMethod; verifiedAt: string; by: string | null } | null;
  response: string | null;
  retentionReason: string | null;
  closingNote: string | null;
  registeredBy: string;
  events: { kind: string; at: string; by: string | null; data: Record<string, unknown> }[];
}

/** A contact found by a request's search in the system. */
export interface RequestSearchMatch {
  id: string;
  name: string;
  matchedBy: RequestSearchCriterion[];
  inTrash: boolean;
  anonymized: boolean;
}

export const OPEN_STATUSES: RequestStatus[] = ['received', 'in_progress', 'in_prevention'];

export const requestKeys = {
  all: ['privacy-requests'] as const,
  list: (status: string, right: string, page: number) =>
    ['privacy-requests', 'list', status, right, page] as const,
  detail: (id: string) => ['privacy-requests', 'detail', id] as const,
  mine: ['privacy-requests', 'mine'] as const,
  contact: (contactId: string) => ['privacy-requests', 'contact', contactId] as const,
  settings: ['privacy-requests', 'settings'] as const,
};

export const useRequests = (status: string, right: string, page: number) =>
  useQuery({
    queryKey: requestKeys.list(status, right, page),
    queryFn: () =>
      api<{ total: number; items: RequestSummary[] }>(
        `/privacy/requests?page=${String(page)}${status ? `&status=${status}` : ''}${right ? `&right=${right}` : ''}`,
      ),
    placeholderData: keepPreviousData,
  });

export const useRequest = (id: string) =>
  useQuery({
    queryKey: requestKeys.detail(id),
    queryFn: () => api<RequestDetail>(`/privacy/requests/${id}`),
    retry: false,
  });

export const useMyRequests = () =>
  useQuery({
    queryKey: requestKeys.mine,
    queryFn: () => api<RequestBrief[]>('/privacy/requests/mine'),
  });

export const useContactRequests = (contactId: string) =>
  useQuery({
    queryKey: requestKeys.contact(contactId),
    queryFn: () => api<RequestBrief[]>(`/contacts/${contactId}/privacy-requests`),
  });

export const usePrivacySettings = () =>
  useQuery({
    queryKey: requestKeys.settings,
    queryFn: () => api<{ preventionPausesTerm: boolean }>('/privacy/settings'),
  });

/** After a change: the request, the lists and the contact (its privacy statuses). */
export async function refreshRequests(client: QueryClient): Promise<void> {
  await Promise.all([
    client.invalidateQueries({ queryKey: requestKeys.all }),
    client.invalidateQueries({ queryKey: ['contacts'] }),
    client.invalidateQueries({ queryKey: ['privacy'] }),
  ]);
}

/** "2026-10-27" → "27/10/2026". */
export function formatDueOn(date: string): string {
  const [y, m, d] = date.split('-');
  return `${d ?? ''}/${m ?? ''}/${y ?? ''}`;
}

/** "Vence en 3 días hábiles" / "Vencida hace 2 días hábiles" / "Vence hoy". */
export function daysLeftLabel(daysLeft: number | null): string | null {
  if (daysLeft === null) return null;
  if (daysLeft === 0) return 'Vence hoy';
  if (daysLeft > 0)
    return `Vence en ${String(daysLeft)} ${daysLeft === 1 ? 'día hábil' : 'días hábiles'}`;
  const late = -daysLeft;
  return `Vencida hace ${String(late)} ${late === 1 ? 'día hábil' : 'días hábiles'}`;
}
